import { useEmployees } from '@/hooks/use-employees';
import { ACTIVE_EMPLOYEE_STATUSES } from '@/lib/employees';
import { cn } from '@/lib/utils';
import type { EmployeeListItem } from '@/types/employee';
import Mention from '@tiptap/extension-mention';
import { ReactRenderer } from '@tiptap/react';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ForwardedRef,
} from 'react';
import { MENTION_HTML_ATTRIBUTES, mentionRenderHTML } from './candidate-note-extensions';

interface MentionItem {
  id: string;
  label: string;
  avatarUrl: string | null;
}

interface MentionListHandle {
  onKeyDown: (event: KeyboardEvent) => boolean;
}

interface MentionListProps {
  items: MentionItem[];
  command: (payload: { id: string; label: string }) => void;
}

const MentionList = forwardRef(function MentionList(
  { items, command }: MentionListProps,
  ref: ForwardedRef<MentionListHandle>
) {
  const [selected, setSelected] = useState(0);

  useEffect(() => setSelected(0), [items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: (event) => {
      if (items.length === 0) return false;
      if (event.key === 'ArrowDown') {
        setSelected((i) => (i + 1) % items.length);
        return true;
      }
      if (event.key === 'ArrowUp') {
        setSelected((i) => (i - 1 + items.length) % items.length);
        return true;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        const item = items[selected];
        if (item) command({ id: item.id, label: item.label });
        return true;
      }
      return false;
    },
  }));

  if (items.length === 0) {
    return (
      <div className="w-[220px] rounded-lg border border-border bg-popover p-2 text-xs text-muted-foreground shadow-lg">
        No matches.
      </div>
    );
  }

  return (
    <div className="max-h-[200px] w-[220px] overflow-y-auto rounded-lg border border-border bg-popover py-1 shadow-lg">
      {items.map((item, i) => (
        <button
          key={item.id}
          type="button"
          className={cn(
            'flex w-full cursor-pointer items-center gap-2.5 px-3 py-1.5 text-left transition-colors',
            i === selected ? 'bg-accent text-accent-foreground' : 'hover:bg-muted'
          )}
          onMouseDown={(e) => {
            e.preventDefault();
            command({ id: item.id, label: item.label });
          }}
          onMouseEnter={() => setSelected(i)}
        >
          {item.avatarUrl ? (
            <img
              src={item.avatarUrl}
              alt=""
              className="h-6 w-6 shrink-0 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-semibold text-muted-foreground">
              {item.label
                .split(/\s+/)
                .map((p) => p[0])
                .join('')
                .slice(0, 2)
                .toUpperCase()}
            </div>
          )}
          <div className="truncate text-sm">{item.label}</div>
        </button>
      ))}
    </div>
  );
});

/**
 * A self-contained Tiptap `Mention` extension wired to the @-mention dropdown:
 * a debounced employee lookup feeds a floating `MentionList` that the editor
 * renders into `document.body`. Shared by the note composer and the note edit
 * modal so both author mentions identically; the read-only feed uses a
 * suggestion-less mention (it only needs to render existing ones).
 *
 * Each call owns its own employee query and popup refs, so two editors mounted
 * at once (composer + edit modal) don't fight over the dropdown.
 */
export function useNoteMentionExtension() {
  const [mentionQuery, setMentionQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQuery(mentionQuery), 200);
    return () => window.clearTimeout(t);
  }, [mentionQuery]);

  const { data: employeesData } = useEmployees(
    debouncedQuery
      ? { search: debouncedQuery, limit: 10, statuses: ACTIVE_EMPLOYEE_STATUSES }
      : { limit: 10, statuses: ACTIVE_EMPLOYEE_STATUSES }
  );

  const employeesRef = useRef<EmployeeListItem[]>([]);
  useEffect(() => {
    employeesRef.current = employeesData?.data ?? [];
  }, [employeesData]);

  const rendererRef = useRef<ReactRenderer<MentionListHandle, MentionListProps> | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);

  // Tiptap's mention suggestion appends the popup directly to document.body and
  // only cleans it up on Escape or onExit. If the component unmounts while the
  // popup is open (navigating away mid-@-mention, StrictMode double-invoke, HMR),
  // that body node can leak. Force-clean on unmount.
  useEffect(() => {
    return () => {
      popupRef.current?.remove();
      popupRef.current = null;
      rendererRef.current?.destroy();
      rendererRef.current = null;
    };
  }, []);

  return useMemo(
    () =>
      Mention.configure({
        HTMLAttributes: MENTION_HTML_ATTRIBUTES,
        renderHTML: mentionRenderHTML,
        suggestion: {
          items: ({ query }) => {
            setMentionQuery(query);
            const q = query.toLowerCase();
            const rows = employeesRef.current;
            const filtered = q
              ? rows.filter((e) => `${e.firstName} ${e.lastName}`.toLowerCase().includes(q))
              : rows;
            return filtered.slice(0, 8).map((e) => ({
              id: e.id,
              label: `${e.firstName} ${e.lastName}`.trim(),
              avatarUrl: e.avatarUrl ?? null,
            }));
          },
          render: () => ({
            onStart: (props) => {
              rendererRef.current = new ReactRenderer(MentionList, {
                props: {
                  items: props.items as MentionItem[],
                  command: props.command as (payload: { id: string; label: string }) => void,
                },
                editor: props.editor,
              });
              if (!props.clientRect) return;
              const rect = props.clientRect();
              if (!rect) return;
              const popup = document.createElement('div');
              popup.style.position = 'fixed';
              popup.style.zIndex = '50';
              popup.style.left = `${rect.left}px`;
              popup.style.top = `${rect.bottom + 4}px`;
              popup.appendChild(rendererRef.current.element as Node);
              document.body.appendChild(popup);
              popupRef.current = popup;
            },
            onUpdate: (props) => {
              rendererRef.current?.updateProps({
                items: props.items as MentionItem[],
                command: props.command as (payload: { id: string; label: string }) => void,
              });
              if (!props.clientRect || !popupRef.current) return;
              const rect = props.clientRect();
              if (!rect) return;
              popupRef.current.style.left = `${rect.left}px`;
              popupRef.current.style.top = `${rect.bottom + 4}px`;
            },
            onKeyDown: (props) => {
              if (props.event.key === 'Escape') {
                popupRef.current?.remove();
                popupRef.current = null;
                rendererRef.current?.destroy();
                rendererRef.current = null;
                return true;
              }
              return rendererRef.current?.ref?.onKeyDown(props.event) ?? false;
            },
            onExit: () => {
              popupRef.current?.remove();
              popupRef.current = null;
              rendererRef.current?.destroy();
              rendererRef.current = null;
            },
          }),
        },
      }),
    []
  );
}
